function _defineProperty(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === 'function') {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty(target, key, source[key]);
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Flex } from "sprout-ui-primitives";
import { useCallback, useMemo, useRef, useState } from "react";
import { FilterList, Add } from "sprout-ui-icons";
import Fuse from "fuse.js";
import { useCombobox } from "downshift";
import { ScrollContainer as BaseScrollContainer } from "sprout-ui-dom/dist/ScrollContainer";
import { Form } from "sprout-ui-dom/dist/Form";
import { useLabelsDialog_CreateLabels_NextMutation } from "sprout-graphql";
import { useSnackbar } from "sprout-ui-dom/dist/Snackbar";
import styled, { css } from "styled-components";
import { media } from "sprout-ui-theme";
import LabelsDialogInput from "./LabelsDialogInput";
import LabelsDialogEmpty from "./LabelsDialogEmpty";
import LabelsDialogLabels from "./LabelsDialogLabels";
const stateReducer = (state, actionAndChanges)=>{
    const { type , changes  } = actionAndChanges;
    if (type === useCombobox.stateChangeTypes.InputBlur) return state;
    if (type === useCombobox.stateChangeTypes.InputChange) return _objectSpread({}, changes, {
        highlightedIndex: 0
    });
    return changes;
};
const LabelsDialogFilterInput = styled(LabelsDialogInput)`
  margin-right: 8px;

  ${({ $hide  })=>$hide && css`
      display: none;
    `}
`;
const LabelsDialogInputsWrapper = styled(Flex)`
  ${media.up("small")} {
    padding-top: 24px;
  }
`;
const ScrollContainer = styled(BaseScrollContainer)`
  ${media.up("small")} {
    padding-bottom: 24px;
  }
`;
const LabelsDialogInner = (props)=>{
    const { labels , alterLabelsForItems: alterLabelsForItemsProp , onRequestClose , rootTopicId ,  } = props;
    const [filterInputValue, setInputValue] = useState("");
    const handleInputValueChange = (changes)=>{
        if (typeof changes.inputValue === "string") setInputValue(changes.inputValue);
    };
    const searcher = useMemo(()=>{
        return new Fuse(labels, {
            keys: [
                "label.text",
                "label.style"
            ],
            threshold: 0
        });
    }, [
        labels
    ]);
    const items = useMemo(()=>{
        return filterInputValue.length === 0 ? labels : searcher.search(filterInputValue).map(({ item  })=>item);
    }, [
        filterInputValue,
        labels,
        searcher
    ]);
    const alterLabelsForItems = useCallback(({ label , state  }, cb)=>{
        alterLabelsForItemsProp({
            variables: {
                removeLabelIds: state === "complete" ? [
                    label.id
                ] : [],
                addLabelIds: state !== "complete" ? [
                    label.id
                ] : []
            }
        });
        if (cb) cb();
    }, [
        alterLabelsForItemsProp
    ]);
    const handleChange = useCallback((changes)=>{
        const { selectedItem  } = changes;
        if (!selectedItem) return;
        alterLabelsForItems(selectedItem, onRequestClose);
    }, [
        alterLabelsForItems,
        onRequestClose
    ]);
    const { getComboboxProps , getMenuProps , getInputProps , getItemProps , reset  } = useCombobox({
        stateReducer,
        /**
       * `defaultIsOpen: true` is required, because without it, the first
       * arrow down event will trigger it to be open, instead of highlighting
       * the next item.
       */ defaultIsOpen: true,
        inputValue: filterInputValue,
        onInputValueChange: handleInputValueChange,
        initialHighlightedIndex: 0,
        items,
        itemToString: useMemo(()=>()=>"", []),
        onSelectedItemChange: handleChange
    });
    const [newInputValue, setNewInputValue] = useState("");
    const newInputValueRef = useRef(newInputValue);
    const handleNewInputValueChange = useCallback((event)=>{
        const { value  } = event.target;
        setNewInputValue(value);
        newInputValueRef.current = value;
    }, []);
    const handleNewCancel = useCallback(()=>{
        setNewInputValue("");
    }, []);
    const showSnackbar = useSnackbar();
    const [createLabel] = useLabelsDialog_CreateLabels_NextMutation();
    const handleNewSubmit = useCallback(()=>{
        createLabel({
            variables: {
                rootTopicId,
                text: newInputValueRef.current
            }
        }).then(({ data  })=>{
            var ref;
            const label = data === null || data === void 0 ? void 0 : (ref = data.createLabel) === null || ref === void 0 ? void 0 : ref.label;
            if (!label) return;
            alterLabelsForItems({
                label,
                state: "incomplete"
            }, onRequestClose);
        }).catch(()=>{
            showSnackbar.error();
        });
    }, [
        alterLabelsForItems,
        createLabel,
        onRequestClose,
        rootTopicId,
        showSnackbar, 
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(LabelsDialogInputsWrapper, _objectSpread({
                onSubmit: handleNewSubmit,
                as: Form
            }, getComboboxProps(), {
                mb: 4,
                children: [
                    /*#__PURE__*/ _jsx(LabelsDialogFilterInput, {
                        onRequestClose: onRequestClose,
                        autoFocus: true,
                        hideOnTouchDevices: true,
                        /**
           * Instead of conditionally rendering the input, we use a $hide prop
           * instead, so that the component works nicely with `downshift`.
           *
           * `downshift` expects the component to always be mounted.
           */ $hide: newInputValue.length !== 0,
                        placeholder: "Filter",
                        prependIcon: FilterList,
                        onCancel: filterInputValue ? reset : undefined,
                        getInputProps: getInputProps
                    }),
                    filterInputValue.length === 0 && /*#__PURE__*/ _jsx(LabelsDialogInput, {
                        onRequestClose: onRequestClose,
                        value: newInputValue,
                        onChange: handleNewInputValueChange,
                        placeholder: "New",
                        prependIcon: Add,
                        onCancel: newInputValue ? handleNewCancel : undefined,
                        onSubmit: newInputValue ? true : undefined
                    })
                ]
            })),
            /*#__PURE__*/ _jsx(ScrollContainer, _objectSpread({
                mx: -1
            }, getMenuProps(), {
                children: labels.length === 0 ? /*#__PURE__*/ _jsx(LabelsDialogEmpty, {}) : /*#__PURE__*/ _jsx(LabelsDialogLabels, {
                    labels: items,
                    getItemProps: getItemProps,
                    onLabelChange: alterLabelsForItems
                })
            }))
        ]
    });
};
export default LabelsDialogInner;
